import { useExperiment, useGrowthBook } from '@growthbook/growthbook-react';
import { useUserContext, useThemeContext } from 'contexts';
import { ThemeNames } from 'types/themes';
import { RECENTLY_SOLD_NAV_LINK_FLAG } from 'constants/feature-flags';
import { IPreferences, PreferencesContext } from 'contexts/preferences';
import { useContext, useEffect, useMemo, useCallback } from 'react';
import getSearchPageParameters from 'utils/get-search-page-redirect-url';
import { trackEvent } from 'utils/google-tag-manager';
import { 
  GTM_EXPERIMENT_A_ALL_SOLD_PRICES_NAV_VIEW, 
  GTM_EXPERIMENT_A_ALL_SOLD_PRICES_NAV_CLICK,
  GTM_EXPERIMENT_B_ALL_SOLD_PRICES_NAV_VIEW,
  GTM_EXPERIMENT_B_ALL_SOLD_PRICES_NAV_CLICK,
  GTM_EXPERIMENT_SOLD_NAV_LINK_VIEW,
  GTM_EXPERIMENT_SOLD_NAV_LINK_CLICK,
} from 'constants/events';

// Experiment variations
const VARIATION_CONTROL = 0; // "A" version (index 0): Current "Sold Prices" experience (Control)
const VARIATION_TEST = 1; // "B" version (index 1): New "Recently Sold" experience


// Link text constants
const RECENTLY_SOLD_TEXT = 'Recently Sold';
const SOLD_PRICES_TEXT = 'Sold Prices';
const SOLD_TEXT = 'Sold';

interface RecentlySoldNavConfig {
  showRecentlySoldLink: boolean;
  soldUrl: string;
  mobileSoldUrl: string;
  soldLinkText: string;
  mobileSoldText: string;
  onSoldLinkClick: () => void;
}

export const useRecentlySoldNav = (): RecentlySoldNavConfig => {
  const { siteLocation, isCrawler } = useUserContext();
  const { themeName } = useThemeContext();
  const { listingParams } = useContext(PreferencesContext) as IPreferences;
  const isZoocasaTenant = themeName === ThemeNames.ZOOCASA;
  const { ready: isGrowthBookReady } = useGrowthBook();

  const shouldTrack = useMemo(() => !isCrawler && isZoocasaTenant, [isCrawler, isZoocasaTenant]);
  
  const recentlySoldExperiment = useExperiment({ 
    key: RECENTLY_SOLD_NAV_LINK_FLAG, 
    variations: [VARIATION_CONTROL, VARIATION_TEST],
    active: shouldTrack && isGrowthBookReady,
  });

  const baseUrl = useMemo(() => `/${siteLocation?.toLowerCase()}-real-estate`, [siteLocation]);

  const showRecentlySoldLink = useMemo(() => {
    // Show "Control" for crawlers or when GrowthBook isn't ready, otherwise use default experiment variation
    if (isCrawler || !isGrowthBookReady) return false;
    return recentlySoldExperiment.variationId === VARIATION_TEST;
  }, [isCrawler, recentlySoldExperiment.variationId, isGrowthBookReady]);

  const soldUrl = useMemo(() => 
    showRecentlySoldLink ? `${baseUrl}/sold` : '/search?status=not-available-sold',
  [showRecentlySoldLink, baseUrl]
  );

  const mobileSoldUrl = useMemo(() => 
    showRecentlySoldLink 
      ? `${baseUrl}/sold`
      : getSearchPageParameters({ 
        ...listingParams, 
        filter: { ...listingParams?.filter, status: 'not-available-sold' }, 
      }),
  [showRecentlySoldLink, baseUrl, listingParams]
  );

  const soldLinkText = useMemo(() => 
    showRecentlySoldLink ? RECENTLY_SOLD_TEXT : SOLD_PRICES_TEXT,
  [showRecentlySoldLink]
  );

  const mobileSoldText = useMemo(() => 
    showRecentlySoldLink ? RECENTLY_SOLD_TEXT : SOLD_TEXT,
  [showRecentlySoldLink]
  );

  useEffect(() => {
    if (!shouldTrack) return;
    // Track general view event for GrowthBook
    trackEvent(GTM_EXPERIMENT_SOLD_NAV_LINK_VIEW);
    // Track view event based on experiment variation
    if (recentlySoldExperiment.variationId === VARIATION_TEST) {
      trackEvent(GTM_EXPERIMENT_B_ALL_SOLD_PRICES_NAV_VIEW);
      return;
    }
    // Track view event for "Control" variation
    trackEvent(GTM_EXPERIMENT_A_ALL_SOLD_PRICES_NAV_VIEW);
  }, [recentlySoldExperiment.variationId, shouldTrack]);
  
  const handleClick = useCallback(() => {
    if (!shouldTrack) return;
    // Track general click event for GrowthBook
    trackEvent(GTM_EXPERIMENT_SOLD_NAV_LINK_CLICK);
    // Track click event based on experiment variation
    if (recentlySoldExperiment.variationId === VARIATION_TEST) {
      trackEvent(GTM_EXPERIMENT_B_ALL_SOLD_PRICES_NAV_CLICK);
      return;
    }
    // Track click event for "Control" variation
    trackEvent(GTM_EXPERIMENT_A_ALL_SOLD_PRICES_NAV_CLICK);
  }, [recentlySoldExperiment.variationId, shouldTrack]);

  return useMemo(() => ({
    showRecentlySoldLink,
    soldUrl,
    mobileSoldUrl,
    soldLinkText,
    mobileSoldText,
    onSoldLinkClick: handleClick,
  }), [showRecentlySoldLink, soldUrl, mobileSoldUrl, soldLinkText, mobileSoldText, handleClick]);
}; 